import React, { useEffect, useRef, useCallback } from 'react';
import styles from './ReservationDetail.module.css';
import { useTranslation } from 'react-i18next';
import { IReservationDetails } from '../../interfaces';
import { getDay, getDate, getMonth, getYear, getNumberOfNights } from '../../utils/utils';
import BookingReservationDetail from '../bookingReservationDetail/BookingReservationDetail';
import { Helmet } from 'react-helmet';
import { NO_INFO_AVAILABLE } from '../../utils/constant';
import BookingReservationRoomDetail from '../bookingReservationRoomDetail/BookingReservationRoomDetail';
import classnames from 'classnames';
interface IReservationDetail {
  reservationDetails: IReservationDetails;
  handleSessionTimeouts: (flag: boolean, errorMessage: string, messageHeader: string) => void;
}

const ReservationDetail = ({ reservationDetails, handleSessionTimeouts }: IReservationDetail) => {
  const { t } = useTranslation();
  const headerRef = useRef<HTMLHeadingElement>(null);
  const activityTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const pageTitleRef = useRef(`${t('bookingHeader')} | Booking.com`);
  const IDLE_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes

  const resetIdleTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    if (activityTimeoutRef.current) {
      clearTimeout(activityTimeoutRef.current);
    }
    activityTimeoutRef.current = setTimeout(() => {
      handleSessionTimeouts(false, 'sessionLogoutHeader', 'sessionLogoutMessage');
      pageTitleRef.current = `${t('LoggedOutTitle')} | Booking.com`;
    }, IDLE_TIMEOUT_MS);
  }, [handleSessionTimeouts, t]);

  const getBookingDetailsHandler = (
    checkInDate: string,
    checkoutDate: string,
    numberOfRooms: string
  ) => {
    return `${t('Nights')}: ${getNumberOfNights(checkInDate, checkoutDate)}, ${t(
      'Rooms'
    )}: ${checkForNoInfo(numberOfRooms)}`;
  };

  const getBookedForDetailsHandler = (nrAdults: string, nrChildren: string) => {
    const adultText =
      nrAdults === '0' || nrAdults === NO_INFO_AVAILABLE
        ? ''
        : `${t('Adults')}: ${checkForNoInfo(nrAdults)}`;
    const childrenText =
      nrChildren === '0' || nrChildren === NO_INFO_AVAILABLE
        ? ''
        : `${t('Children')}: ${checkForNoInfo(nrChildren)}`;

    return [adultText, childrenText].filter(Boolean).join(', ');
  };

  const checkForNoInfo = (val: string) => {
    if (val === NO_INFO_AVAILABLE) {
      return t('NoInfoAvailable');
    } else {
      return val;
    }
  };

  const checkForNoInfoForNumberValueKey = (val: string) => {
    if (val === NO_INFO_AVAILABLE) {
      return '0';
    } else {
      return val;
    }
  };

  const reformatReservationId = (reservationId: string) => {
    const idArray = Array.from(reservationId);
    return `${idArray.slice(0, 4).join('')}.${idArray.slice(4, 7).join('')}.${idArray
      .slice(7, 10)
      .join('')}`;
  };

  const bookingReservationStatus = classnames(styles.bookingReservationDetailsStatusTextColor, {
    [styles.bookingCancelledReservationStatusTextColor]:
      reservationDetails.reservationstatus === 'Cancelled'
  });

  useEffect(() => {
    headerRef.current?.focus();
    resetIdleTimer();

    // Throttled activity handler - updates at most once per second
    let lastUpdate = 0;
    const handleActivity = () => {
      const now = Date.now();
      if (now - lastUpdate > 1000) {
        lastUpdate = now;
        resetIdleTimer();
      }
    };

    window.addEventListener('click', handleActivity);
    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('keydown', handleActivity);

    return () => {
      if (activityTimeoutRef.current) {
        clearTimeout(activityTimeoutRef.current);
      }
      window.removeEventListener('click', handleActivity);
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('keydown', handleActivity);
    };
  }, [resetIdleTimer]);

  return (
    <div className={styles.reservationDetailContainer} data-testid={'reservationDetailDiv'}>
      <Helmet>
        <title>{pageTitleRef.current}</title>
      </Helmet>
      <div className={styles.yourBookingDetail}>
        <h1 ref={headerRef} tabIndex={-1} className={styles.yourBookingDetailHeader}>
          <strong>{t('bookingHeader')}</strong>
        </h1>
        <BookingReservationDetail
          bookingLabel={'Confirmation Number'}
          bookingValue={reformatReservationId(reservationDetails.reservationId)}
          bookingValueTextStyle={styles.bookingReservationDetailsTextColor}
        />
        <BookingReservationDetail
          bookingLabel={'Pin code'}
          bookingValue={reservationDetails.pinCode}
          bookingValueTextStyle={styles.bookingReservationDetailsTextColor}
        />
        <BookingReservationDetail
          bookingLabel={'Number of rooms and nights booked'}
          bookingValue={getBookingDetailsHandler(
            reservationDetails.checkInDate,
            reservationDetails.checkOutDate,
            checkForNoInfoForNumberValueKey(reservationDetails.numberOfRooms)
          )}
          bookingValueTextStyle={styles.bookingReservationDetailsTextColor}
        />
        {(reservationDetails.nrAdults !== NO_INFO_AVAILABLE &&
          reservationDetails.nrAdults !== '0') ||
        (reservationDetails.nrChildren !== NO_INFO_AVAILABLE &&
          reservationDetails.nrChildren !== '0') ? (
          <BookingReservationDetail
            bookingLabel={'Number of guests'}
            bookingValue={getBookedForDetailsHandler(
              checkForNoInfoForNumberValueKey(reservationDetails.nrAdults),
              checkForNoInfoForNumberValueKey(reservationDetails.nrChildren)
            )}
            bookingValueTextStyle={styles.bookingReservationDetailsTextColor}
          />
        ) : null}
        <BookingReservationDetail
          bookingLabel={'Check-in'}
          bookingValue={`${t(getDay(reservationDetails.checkInDate))}, ${t(
            getDate(reservationDetails.checkInDate)
          )} ${t(getMonth(reservationDetails.checkInDate))} ${t(
            getYear(reservationDetails.checkInDate)
          )}`}
          bookingValueTextStyle={styles.bookingReservationDetailsTextColor}
        />
        <BookingReservationDetail
          bookingLabel={'Check-out'}
          bookingValue={`${t(getDay(reservationDetails.checkOutDate))}, ${t(
            getDate(reservationDetails.checkOutDate)
          )} ${t(getMonth(reservationDetails.checkOutDate))} ${t(
            getYear(reservationDetails.checkOutDate)
          )}`}
          bookingValueTextStyle={styles.bookingReservationDetailsTextColor}
        />

        <BookingReservationDetail
          bookingLabel={'Status'}
          bookingValue={`${t(reservationDetails.reservationstatus)}`}
          bookingValueTextStyle={bookingReservationStatus}
        />
      </div>
      <div className={styles.propertyDetail}>
        <h2 className={styles.propertyDetailHeader}>
          <strong>{t('bookingHeader_property')}</strong>
        </h2>
        <BookingReservationDetail
          bookingLabel={'Name'}
          bookingValue={checkForNoInfo(reservationDetails.hotelName)}
          bookingValueTextStyle={styles.bookingReservationDetailsTextColor}
        />
        {reservationDetails.statusList.length > 0 && (
          <BookingReservationRoomDetail roomStatusList={reservationDetails.statusList} />
        )}

        <p className={styles.bookingReservationInfoText}>{`${t(
          'FurtherReservationDetailsInfo'
        )}`}</p>
      </div>
    </div>
  );
};

export default ReservationDetail;
